import ListCard from '@Components/ListCard/ListCard';
import AssetInputModal from '@Components/Modals/AssetInput/AssetInputModal';
import DeleteAssetTemplate from '@Components/Modals/AssetTemplate/DeleteAssetTemplate';
import AssetTemplateServices from '@Services/AssetTemplate.services';
import { AssetTemplate } from '@Types/Asset.types';
import { PaginatedResponse } from '@Types/Response.types';
import {
  ASSET_MODAL_TYPE,
  PAGE_PROP,
  PAGE_SIZE,
  PAGINATED_DEFAULT,
  PATHS,
  SEARCH_PROP,
  TABLE_STRUCTURES,
} from '@Utils/constants';
import { formatCreateAssetTemplateValues } from '@Utils/form.utils';
import { handleRequestError } from '@Utils/request.utils';
import { Button, Icons, Pagination, SearchBox, SectionCard, Toast, useTopHat } from '@kg-internal/pv3-uikit';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const AssetTemplates = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [assetTemplates, setAssetTemplates] = useState<PaginatedResponse<AssetTemplate>>(PAGINATED_DEFAULT);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>();
  const currentPage = Number(searchParams.get(PAGE_PROP) ?? '1');
  const searchQuery = searchParams.get(SEARCH_PROP) ?? '';
  const [showDeleteAssetTemplate, setShowDeleteAssetTemplate] = useState<boolean>(false);
  const [selectedAssetTemplate, setSelectedAssetTemplate] = useState<AssetTemplate | undefined>(undefined);
  const [showNewAssetTemplateModal, setShowNewAssetTemplateModal] = useState<boolean>(false);

  useTopHat({
    activeNav: 'nav-settings',
    activeTab: undefined,
    tabs: [],
    breadcrumbs: [
      { title: 'Settings', href: PATHS.SETTINGS, id: 'Settings' },
      {
        title: 'Asset Templates',
        href: `${PATHS.ASSET_TEMPLATES}?page=${1}`,
        id: 'Asset Templates',
      },
    ],
  });

  const handleNewPage = (newPage: number, data: PaginatedResponse<AssetTemplate>) => {
    if (newPage <= 0 || newPage > assetTemplates.totalPages) {
      return;
    }
    const page = newPage > data.totalPages ? data.prevPage : newPage;
    if (currentPage !== page) {
      searchParams.set(PAGE_PROP, page?.toString() || '1');
      setSearchParams(searchParams);
    }
  };

  const getAssetTemplates = () => {
    setSelectedAssetTemplate(undefined);
    AssetTemplateServices.searchAssetTemplate({ search: searchQuery }, currentPage)
      .then(({ data }) => {
        setAssetTemplates(data);
        setErrorMessage(undefined);
        handleNewPage(currentPage, data);
      })
      .catch((error) => {
        handleRequestError(error, setErrorMessage);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    setIsLoading(true);
    getAssetTemplates();
  }, [searchQuery, currentPage]);

  const handleSearchQueryChange = (newValue: string) => {
    searchParams.set(SEARCH_PROP, newValue);
    searchParams.set(PAGE_PROP, '1');
    if (!newValue) {
      searchParams.delete(SEARCH_PROP);
    }
    setSearchParams(searchParams);
  };

  const handleAssetTemplateAction = (id: string, setModal: (show: boolean) => void) => {
    setSelectedAssetTemplate(assetTemplates.docs.find((assetTempl: AssetTemplate) => assetTempl.id === id));
    setModal(true);
  };
  const handleAssetTemplateSubmit = (formData: FormData) => {
    if (selectedAssetTemplate) {
      return AssetTemplateServices.editAssetTemplate(selectedAssetTemplate.id, formData);
    }
    return AssetTemplateServices.createAssetTemplate(formData);
  };
  const onActionSuccess = () => {
    Toast.success(selectedAssetTemplate ? 'Asset Template successfully updated' : 'Asset Template successfully created');
    getAssetTemplates();
  };
  return (
    <div className='grid'>
      <div className='col-12 col-md-3'>
        <SectionCard>
          <SearchBox
            id='search-tax-status'
            fullWidth
            focusOnRender
            value={searchQuery}
            defaultValue={searchQuery}
            onChange={handleSearchQueryChange}
          />
          <Button
            type='primary'
            className='button-with-icon'
            fullWidth
            onClick={() => setShowNewAssetTemplateModal(true)}>
            {Icons.Plus} Add new Asset Template
          </Button>
        </SectionCard>
      </div>
      <div className='col-12 col-md-9'>
        <ListCard<AssetTemplate>
          title={'Asset Templates'}
          structure={TABLE_STRUCTURES.ASSET_TEMPLATES}
          data={assetTemplates.docs}
          loading={isLoading}
          withActions={{
            edit: (editId) => handleAssetTemplateAction(editId, setShowNewAssetTemplateModal),
            delete: (deleteId) => handleAssetTemplateAction(deleteId, setShowDeleteAssetTemplate),
          }}
          error={errorMessage}
          searchEntered={!!searchQuery}
        />
      </div>
      <div className='col-12 content-middle content-right-md'>
        <Pagination
          count={assetTemplates.totalDocs}
          perPage={PAGE_SIZE}
          currentPage={currentPage - 1}
          onChange={(page) => handleNewPage(page + 1, assetTemplates)}
        />
      </div>
      <AssetInputModal
        show={showNewAssetTemplateModal}
        onClose={() => {
          setShowNewAssetTemplateModal(false);
          setSelectedAssetTemplate(undefined);
        }}
        onSuccess={onActionSuccess}
        title={selectedAssetTemplate ? 'Edit Asset Template' : 'Create Asset Template'}
        onSubmit={handleAssetTemplateSubmit}
        formatter={formatCreateAssetTemplateValues}
        type={ASSET_MODAL_TYPE.ASSET_TEMPLATE}
        assetTemplate={selectedAssetTemplate}
      />
      <DeleteAssetTemplate
        show={showDeleteAssetTemplate}
        assetTemplate={selectedAssetTemplate}
        onClose={() => {
          setShowDeleteAssetTemplate(false);
          setSelectedAssetTemplate(undefined);
        }}
        onSuccess={() => getAssetTemplates()}
      />
    </div>
  );
};

export default AssetTemplates;
